import { getDanceStyles, getDanceStyleColor, getDanceStyleLabelColor } from "../config/danceStyles";
import "./DanceStyleChips.css";

/**
 * Inline, always-visible chip picker for dance styles.
 *
 * One component for every surface where a user PICKS styles: business registration,
 * org settings, the instructor profile editor, and the student branch of registration.
 * Each of those previously re-inlined its own markup against one of three different
 * chip stylesheets, so the same control had three resting appearances.
 *
 * Colour spec: the chip carries its style's own colour in BOTH states.
 *   unselected — white fill, border in the style colour, label in the style colour
 *   selected   — solid style-colour fill, white label
 * The versions this replaces used a neutral grey or purple border when unselected, so
 * the palette only appeared once something was clicked. Showing it up front is the
 * point: the colours are what make the row worth engaging with.
 *
 * Unselected labels resolve through getDanceStyleLabelColor, which keeps the hue
 * wherever it can carry text on white and falls back to standard dark text where it
 * cannot. Borders and selected fills always use the true config colour.
 *
 * Read-only style tags on public-facing cards (instructor cards, school cards, the
 * public profile) are deliberately NOT this component — they stay a uniform colour.
 *
 * @param {string[]} selected Canonical slug ids currently selected.
 * @param {(ids: string[]) => void} onChange Receives the full next selection.
 */
function DanceStyleChips({ selected = [], onChange }) {
    const toggle = id => {
        onChange(selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id]);
    };

    return (
        <div className="dance-style-chips">
            {getDanceStyles().map(style => {
                const isSelected = selected.includes(style.id);
                const color = getDanceStyleColor(style.id);
                return (
                    <button
                        key={style.id}
                        type="button"
                        className={`dance-style-chips-chip${isSelected ? " selected" : ""}`}
                        aria-pressed={isSelected}
                        style={{
                            borderColor: color,
                            ...(isSelected
                                ? { background: color }
                                : { color: getDanceStyleLabelColor(style.id) }),
                        }}
                        onClick={() => toggle(style.id)}>
                        {style.label}
                    </button>
                );
            })}
        </div>
    );
}

export default DanceStyleChips;
